// skipcq: JS-W1028
import { useEffect, useState, useCallback } from 'react';
import {
  arrayRemove,
  arrayUnion,
  collection,
  getDocs,
  addDoc,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  deleteField,
  updateDoc,
} from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import { db } from '../utils/firebase';
import { EVENT_DEFINITIONS_COLLECTION } from '../utils/firestoreCollections';
import { isValidIanaTimeZone } from '../domain/eventTimezones';
import {
  isValidEventLocalDateString,
  isValidOptionalEventHttpsLink,
  normalizeOptionalEventTimeRange,
} from '../domain/eventDefinitionValidation';

function requireNonEmptyString(value, errorMessage) {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    throw new Error(errorMessage);
  }
  return trimmed;
}

function normalizeEventTimesOrThrow(eventStartTime, eventEndTime) {
  try {
    return normalizeOptionalEventTimeRange(eventStartTime, eventEndTime);
  } catch (timeErr) {
    throw new Error(
      timeErr?.message && typeof timeErr.message === 'string'
        ? timeErr.message
        : 'Invalid event times.',
    );
  }
}

function trimOptionalStrings(fields) {
  return {
    locationTrimmed: String(fields.location ?? '').trim(),
    cityStateTrimmed: String(fields.cityState ?? '').trim(),
    linkTrimmed: String(fields.link ?? '').trim(),
    linkTextTrimmed: String(fields.linkText ?? '').trim(),
    descriptionTrimmed: String(fields.description ?? '').trim(),
    latitudeTrimmed: String(fields.latitude ?? '').trim(),
    longitudeTrimmed: String(fields.longitude ?? '').trim(),
  };
}

/**
 * True when `updateDoc` / Firestore failed because the document was missing.
 * @param {unknown} err
 * @returns {boolean}
 */
function isFirestoreMissingDocumentError(err) {
  if (!err || typeof err !== 'object') {
    return false;
  }
  if (err.code === 'not-found') {
    return true;
  }
  const message = String('message' in err ? err.message : '');
  return /no document to update/i.test(message);
}

function sortEvents(rows) {
  return [...rows].sort((a, b) => {
    const dateA = String(a.eventLocalDate ?? '').trim();
    const dateB = String(b.eventLocalDate ?? '').trim();
    if (dateA !== dateB) {
      return dateA.localeCompare(dateB);
    }
    const tzA = String(a.eventTimezone ?? '').trim();
    const tzB = String(b.eventTimezone ?? '').trim();
    if (tzA !== tzB) {
      return tzA.localeCompare(tzB);
    }
    return String(a.name ?? '').localeCompare(String(b.name ?? ''));
  });
}

/**
 * @param {{
 *   name: unknown,
 *   host: unknown,
 *   image: unknown,
 *   cardImage: unknown,
 *   headerImage: unknown,
 *   eventLocalDate: unknown,
 *   eventTimezone: unknown,
 *   eventStartTime: unknown,
 *   eventEndTime: unknown,
 *   location: unknown,
 *   cityState: unknown,
 *   link: unknown,
 *   linkText: unknown,
 *   description: unknown,
 *   latitude: unknown,
 *   longitude: unknown,
 * }} fields
 */
function validateAndNormalizeFields({
  name,
  host,
  image,
  cardImage,
  headerImage,
  eventLocalDate,
  eventTimezone,
  eventStartTime,
  eventEndTime,
  location,
  cityState,
  link,
  linkText,
  description,
  latitude,
  longitude,
}) {
  const nameTrimmed = requireNonEmptyString(name, 'Name is required.');
  const hostTrimmed = requireNonEmptyString(host, 'Host is required.');
  const imageForCard = image ?? cardImage;
  const imageTrimmed = requireNonEmptyString(
    imageForCard,
    'Choose a card image from the list.',
  );
  const headerImageTrimmed = requireNonEmptyString(
    headerImage,
    'Choose a header image from the list.',
  );

  const dateTrimmed = String(eventLocalDate ?? '').trim();
  if (!dateTrimmed || !isValidEventLocalDateString(dateTrimmed)) {
    throw new Error('Event date must be a valid YYYY-MM-DD calendar date.');
  }

  const tzTrimmed = String(eventTimezone ?? '').trim();
  if (!tzTrimmed || !isValidIanaTimeZone(tzTrimmed)) {
    throw new Error('Choose a valid IANA timezone.');
  }

  const timesNormalized = normalizeEventTimesOrThrow(
    eventStartTime,
    eventEndTime,
  );

  const trimmed = trimOptionalStrings({
    location,
    cityState,
    link,
    linkText,
    description,
    latitude,
    longitude,
  });
  if (trimmed.linkTrimmed && !isValidOptionalEventHttpsLink(trimmed.linkTrimmed)) {
    throw new Error('Event link must be a valid https:// URL.');
  }

  return {
    name: nameTrimmed,
    host: hostTrimmed,
    image: imageTrimmed,
    headerImage: headerImageTrimmed,
    eventLocalDate: dateTrimmed,
    eventTimezone: tzTrimmed,
    eventStartTime: timesNormalized.eventStartTime,
    eventEndTime: timesNormalized.eventEndTime,
    location: trimmed.locationTrimmed,
    cityState: trimmed.cityStateTrimmed,
    link: trimmed.linkTrimmed,
    linkText: trimmed.linkTextTrimmed,
    description: trimmed.descriptionTrimmed,
    latitude: trimmed.latitudeTrimmed,
    longitude: trimmed.longitudeTrimmed,
  };
}

/**
 * Fetches event definitions from `eventDefinitions` (create/update; no delete).
 */
function useEventDefinitionsList() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const collRef = collection(db, EVENT_DEFINITIONS_COLLECTION);
      const snapshot = await getDocs(collRef);
      const rows = snapshot.docs.map((docSnapshot) => ({
        id: docSnapshot.id,
        ...docSnapshot.data(),
      }));
      setEvents(sortEvents(rows));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load event definitions', err);
      Sentry.captureException(err);
      setError('Failed to load event definitions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createEvent = useCallback(async (data) => {
    const normalized = validateAndNormalizeFields(data);

    const payload = {
      ...normalized,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      setError(null);
      const collRef = collection(db, EVENT_DEFINITIONS_COLLECTION);
      const ref = await addDoc(collRef, payload);
      const freshSnap = await getDoc(ref);
      const freshData = freshSnap.exists()
        ? { id: ref.id, ...freshSnap.data() }
        : { id: ref.id, ...payload };

      setEvents((prev) => sortEvents([...prev, freshData]));
      return ref.id;
    } catch (err) {
      setError('Failed to create event definition');
      throw err;
    }
  }, []);

  const updateEvent = useCallback(async (id, data) => {
    const normalized = validateAndNormalizeFields(data);

    const updates = {
      ...normalized,
      cardImage: deleteField(),
      venueName: deleteField(),
      updatedAt: serverTimestamp(),
    };

    const ref = doc(db, EVENT_DEFINITIONS_COLLECTION, id);

    try {
      setError(null);
      await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(ref);
        if (!snapshot.exists()) {
          throw new Error('Event not found');
        }
        transaction.update(ref, updates);
      });

      const snap = await getDoc(ref);
      if (!snap.exists()) {
        throw new Error('Event not found');
      }
      const fresh = { id, ...snap.data() };
      setEvents((prev) =>
        sortEvents(prev.map((e) => (e.id === id ? fresh : e))),
      );
    } catch (err) {
      if (err?.message !== 'Event not found') {
        setError('Failed to update event definition');
      }
      throw err;
    }
  }, []);

  const appendFeaturedBeersToEvent = useCallback(async (eventId, beerIds) => {
    const idTrimmed = String(eventId ?? '').trim();
    if (!idTrimmed) {
      throw new Error('Event is required.');
    }
    const idList = Array.isArray(beerIds) ? beerIds : [];
    const uniqueIds = [...new Set(idList.map((beerId) => String(beerId ?? '').trim()).filter(Boolean))];
    if (uniqueIds.length === 0) {
      throw new Error('At least one beer is required.');
    }

    const ref = doc(db, EVENT_DEFINITIONS_COLLECTION, idTrimmed);

    try {
      setError(null);
      await updateDoc(ref, {
        featuredBeers: arrayUnion(...uniqueIds),
        updatedAt: serverTimestamp(),
      });

      const snap = await getDoc(ref);
      if (!snap.exists()) {
        throw new Error('Event not found');
      }
      const fresh = { id: idTrimmed, ...snap.data() };
      setEvents((prev) => {
        const hasRow = prev.some((eventRow) => eventRow.id === idTrimmed);
        if (!hasRow) {
          return sortEvents([...prev, fresh]);
        }
        return sortEvents(
          prev.map((eventRow) => (eventRow.id === idTrimmed ? fresh : eventRow)),
        );
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('appendFeaturedBeersToEvent', err);
      Sentry.captureException(err);
      if (isFirestoreMissingDocumentError(err)) {
        setError('Event not found');
      } else if (err?.message !== 'Event not found') {
        setError('Failed to update featured beers');
      }
      throw err;
    }
  }, []);

  const removeFeaturedBeersFromEvent = useCallback(async (eventId, beerIds) => {
    const idTrimmed = String(eventId ?? '').trim();
    if (!idTrimmed) {
      throw new Error('Event is required.');
    }
    const idList = Array.isArray(beerIds) ? beerIds : [];
    const uniqueIds = [...new Set(idList.map((beerId) => String(beerId ?? '').trim()).filter(Boolean))];
    if (uniqueIds.length === 0) {
      throw new Error('At least one beer is required.');
    }

    const ref = doc(db, EVENT_DEFINITIONS_COLLECTION, idTrimmed);

    try {
      setError(null);
      await updateDoc(ref, {
        featuredBeers: arrayRemove(...uniqueIds),
        updatedAt: serverTimestamp(),
      });

      const snap = await getDoc(ref);
      if (!snap.exists()) {
        throw new Error('Event not found');
      }
      const fresh = { id: idTrimmed, ...snap.data() };
      setEvents((prev) => {
        const hasRow = prev.some((eventRow) => eventRow.id === idTrimmed);
        if (!hasRow) {
          return sortEvents([...prev, fresh]);
        }
        return sortEvents(
          prev.map((eventRow) => (eventRow.id === idTrimmed ? fresh : eventRow)),
        );
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('removeFeaturedBeersFromEvent', err);
      Sentry.captureException(err);
      if (isFirestoreMissingDocumentError(err)) {
        setError('Event not found');
      } else if (err?.message !== 'Event not found') {
        setError('Failed to update featured beers');
      }
      throw err;
    }
  }, []);

  return {
    events,
    loading,
    error,
    refresh,
    createEvent,
    updateEvent,
    appendFeaturedBeersToEvent,
    removeFeaturedBeersFromEvent,
  };
}

export default useEventDefinitionsList;
